/* ═══════════════════════════════════════════════════════════════════
   AUTO-GIFT SUBSCRIPTION SYSTEM - COMPLETE FUNCTIONALITY EXTRACTION
   ────────────────────────────────────────────────────────────────
   Preserves 100% of original functionality while enabling caching
   ═══════════════════════════════════════════════════════════════════ */

(function () {
  /* ═══════════════════════════════════════════════════════════════════
     CONSTANTS & CONFIGURATION
  ═══════════════════════════════════════════════════════════════════ */
  const GIFT_MAPPING_KEY = "giftProductMappings";
  const KERATIN_PRODUCT_ID = 5518567702677;
  const KERATIN_HANDLE = "at-home-keratin-treatment-kit";

  // Cache frequently used selectors
  const CART_BUTTON_SELECTORS =
    "[data-add-to-cart], .product-form__cart-submit, .shopify-payment-button__button";
  const WIDGET_SELECTORS =
    "[data-subscription], .subscription-widget, .selling-plan-group, [data-selling-plan], .subscription-options, .rc_widget, .appstle_subscription, .subscription_widget";
  const REMOVE_BUTTON_SELECTORS =
    '[data-remove-item], [data-cart-remove], .cart-remove, .remove-item, [href*="cart/change"], [data-line-item-remove], [href*="/cart/update"], .btn--remove, [data-quantity="0"], .cart__remove';
  const CART_ELEMENT_SELECTORS = '[class*="cart"], [id*="cart"]';
  const BUTTON_TEXT_SELECTORS = "[data-add-to-cart-text], span";
  const CART_FORM_SELECTORS = 'form[action*="/cart/add"]';
  const VARIANT_INPUT_SELECTORS = 'select[name="id"], input[name="id"]';
  const ONE_TIME_PURCHASE_SELECTORS = 'input[value="onetime"]:checked';
  const ONE_TIME_ACTIVE_SELECTORS =
    ".rc_widget__option--onetime.rc_widget__option--active, .rc-option__onetime.rc-option--active";

  let cartButtons = null;
  let isKeratinProduct = null;

  // Flag to track when we're doing custom cart operations
  let isCustomCartOperation = false;

  // Reload safeguard to prevent infinite loops
  const RELOAD_STORAGE_KEY = "giftAutoReloadCount";
  const MAX_RELOADS = 3;
  const RELOAD_RESET_TIME = 30000; // 30 seconds

  const reloadSafeguard = {
    canReload() {
      try {
        const stored = sessionStorage.getItem(RELOAD_STORAGE_KEY);
        if (!stored) return true;

        const data = JSON.parse(stored);
        const now = Date.now();

        // Reset counter if enough time has passed
        if (now - data.timestamp > RELOAD_RESET_TIME) {
          sessionStorage.removeItem(RELOAD_STORAGE_KEY);
          return true;
        }

        return data.count < MAX_RELOADS;
      } catch (e) {
        return true;
      }
    },

    incrementAndReload() {
      if (!this.canReload()) {
        // Performance: Console log removed
        return false;
      }

      try {
        const stored = sessionStorage.getItem(RELOAD_STORAGE_KEY);
        const data = stored
          ? JSON.parse(stored)
          : { count: 0, timestamp: Date.now() };
        data.count++;
        data.timestamp = Date.now();
        sessionStorage.setItem(RELOAD_STORAGE_KEY, JSON.stringify(data));

        setTimeout(() => location.reload(), 1000);
        return true;
      } catch (e) {
        setTimeout(() => location.reload(), 1000);
        return true;
      }
    },
  };

  /* ═══════════════════════════════════════════════════════════════════
     UTILITY FUNCTIONS
  ═══════════════════════════════════════════════════════════════════ */
  // Simple memoization for performance
  const memoize = (fn, cacheTime = 500) => {
    let cache = null;
    let lastCall = 0;
    return (...args) => {
      const now = Date.now();
      if (cache && now - lastCall < cacheTime) {
        return cache;
      }
      cache = fn(...args);
      lastCall = now;
      return cache;
    };
  };

  // Debouncing for performance optimization
  const debounce = (fn, delay) => {
    let timeoutId;
    return (...args) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => fn(...args), delay);
    };
  };

  // RAF utility for smooth UI updates
  const rafUpdate = (fn) => {
    if (window.requestAnimationFrame) {
      requestAnimationFrame(fn);
    } else {
      setTimeout(fn, 16); // 60fps fallback
    }
  };

  // Cart API with caching for performance
  let cartCache = null;
  let cartCacheTime = 0;
  const CART_CACHE_DURATION = 200; // 200ms cache

  // Reusable objects for performance
  const CART_HEADERS = { "Content-Type": "application/json" };
  const CART_POST_OPTIONS = { method: "POST", headers: CART_HEADERS };

  const cartGET = async () => {
    const now = Date.now();
    if (cartCache && now - cartCacheTime < CART_CACHE_DURATION) {
      return cartCache;
    }
    cartCache = await fetch("/cart.js").then((r) => r.json());
    cartCacheTime = now;
    return cartCache;
  };

  const cartADD = async (payload) => {
    // Invalidate cache when modifying cart
    cartCache = null;
    cartCacheTime = 0;
    return await fetch("/cart/add.js", {
      ...CART_POST_OPTIONS,
      body: JSON.stringify(payload),
    }).then((r) => r.json());
  };

  const cartUPDATE = async (updates) => {
    // Invalidate cache when modifying cart
    cartCache = null;
    cartCacheTime = 0;
    return await fetch("/cart/update.js", {
      ...CART_POST_OPTIONS,
      body: JSON.stringify({ updates }),
    }).then((r) => r.json());
  };

  // Check if current product is keratin treatment kit (cached)
  const checkIsKeratinProduct = () => {
    if (isKeratinProduct === null) {
      isKeratinProduct =
        window.location.pathname.includes(KERATIN_HANDLE) ||
        (window.currentPageGift &&
          window.currentPageGift.productId === KERATIN_PRODUCT_ID);
    }
    return isKeratinProduct;
  };

  // Get current page gift info (memoized for performance)
  const getCurrentPageGift = memoize(
    () => window.currentPageGift || null,
    1000
  );

  /* ═══════════════════════════════════════════════════════════════════
     GIFT MAPPING SYSTEM (localStorage management)
  ═══════════════════════════════════════════════════════════════════ */
  const giftMappings = (() => {
    let cache = null;
    let lastFetch = 0;
    const CACHE_DURATION = 30000; // 30 seconds

    return {
      get() {
        const now = Date.now();
        if (cache && now - lastFetch < CACHE_DURATION) return cache;

        try {
          const stored = localStorage.getItem(GIFT_MAPPING_KEY);
          if (stored) {
            const mappings = JSON.parse(stored);
            const cutoff = now - 7 * 24 * 60 * 60 * 1000;

            // Clean old mappings
            for (const key in mappings) {
              if (mappings[key].timestamp < cutoff) {
                delete mappings[key];
              }
            }

            cache = mappings;
            lastFetch = now;
            return mappings;
          }
        } catch (e) {}

        cache = {};
        lastFetch = now;
        return cache;
      },

      save(giftVariantId, productId, giftTitle) {
        try {
          const mappings = this.get();
          mappings[giftVariantId] = {
            productId,
            giftTitle,
            timestamp: Date.now(),
          };
          localStorage.setItem(GIFT_MAPPING_KEY, JSON.stringify(mappings));
          cache = mappings;
        } catch (e) {}
      },

      remove(giftVariantId) {
        try {
          const mappings = this.get();
          delete mappings[giftVariantId];
          localStorage.setItem(GIFT_MAPPING_KEY, JSON.stringify(mappings));
          cache = mappings;
        } catch (e) {}
      },
    };
  })();

  /* ═══════════════════════════════════════════════════════════════════
     KERATIN PRODUCT BUTTON MANAGEMENT (disable/enable & redirect)
  ═══════════════════════════════════════════════════════════════════ */
  const buttonManager = (() => {
    const getButtons = () => {
      if (!cartButtons) {
        cartButtons = document.querySelectorAll(CART_BUTTON_SELECTORS);
      }
      return cartButtons;
    };

    return {
      disable() {
        if (!checkIsKeratinProduct()) return;

        rafUpdate(() => {
          getButtons().forEach((button) => {
            button.disabled = true;
            button.style.cssText = "opacity: 0.5; cursor: not-allowed;";

            const textElement = button.querySelector(BUTTON_TEXT_SELECTORS);
            if (textElement && !button.dataset.originalText) {
              button.dataset.originalText = textElement.textContent;
              textElement.textContent = "Loading subscription options...";
            }
          });
        });
      },
      enable() {
        if (!checkIsKeratinProduct()) return;

        rafUpdate(() => {
          getButtons().forEach((button) => {
            button.disabled = false;
            button.style.cssText = "";

            const textElement = button.querySelector(BUTTON_TEXT_SELECTORS);
            if (textElement && button.dataset.originalText) {
              textElement.textContent = button.dataset.originalText;
            }
          });
        });

        // Add redirect functionality for keratin product
        this.setupRedirect();
      },

      setupRedirect() {
        if (!checkIsKeratinProduct()) return;

        // Remove existing listeners to avoid duplicates
        getButtons().forEach((button) => {
          const existingHandler = button._keratinClickHandler;
          if (existingHandler) {
            button.removeEventListener("click", existingHandler);
          }
        });

        // Add click handlers for redirect functionality
        getButtons().forEach((button) => {
          const clickHandler = async (e) => {
            // Only handle if this is for the keratin product
            if (!checkIsKeratinProduct()) return;

            // Get current page gift info
            const pageGift = getCurrentPageGift();
            if (!pageGift) return;

            // Prevent default add to cart and handle manually
            e.preventDefault();
            e.stopPropagation();

            try {
              // Get the form data to understand what's being added
              const form =
                button.closest("form") ||
                document.querySelector(CART_FORM_SELECTORS);
              let variantId,
                quantity = 1,
                sellingPlan;

              if (form) {
                const formData = new FormData(form);
                variantId = formData.get("id") || formData.get("variant_id");
                quantity = parseInt(formData.get("quantity") || "1");
                sellingPlan = formData.get("selling_plan");
              } else {
                // Fallback: try to find variant ID from page context
                const variantSelect = document.querySelector(
                  VARIANT_INPUT_SELECTORS
                );
                if (variantSelect) {
                  variantId = variantSelect.value;
                }
              }

              if (!variantId) {
                // Performance: Console log removed
                return;
              }

              // Disable button to prevent double clicks
              button.disabled = true;
              const textElement = button.querySelector(BUTTON_TEXT_SELECTORS);
              const originalText = textElement ? textElement.textContent : "";
              if (textElement) {
                textElement.textContent = "Adding to cart...";
              }

              // Performance: Console log removed

              // Prepare main product item
              const mainItem = {
                id: parseInt(variantId),
                quantity: quantity,
              };

              // Add selling plan if present
              if (sellingPlan) {
                mainItem.selling_plan = parseInt(sellingPlan);
              }

              // Add gift first, then main product
              const success = await addToCartWithGiftFirst(
                [mainItem],
                pageGift.giftVariantId,
                pageGift.giftTitle,
                pageGift.productId
              );

              if (success) {
                // Performance: Console log removed
                // Wait a moment for cart to update, then redirect
                setTimeout(() => {
                  window.location.href = "/cart";
                }, 500);
              } else {
                // Performance: Console log removed
                // Re-enable button on failure
                button.disabled = false;
                if (textElement && originalText) {
                  textElement.textContent = originalText;
                }
              }
            } catch (e) {
              // Performance: Console log removed
              // Re-enable button on error
              button.disabled = false;
              const textElement = button.querySelector(BUTTON_TEXT_SELECTORS);
              if (textElement && originalText) {
                textElement.textContent = originalText;
              }
            }
          };

          // Store reference to handler for cleanup
          button._keratinClickHandler = clickHandler;
          button.addEventListener("click", clickHandler);
        });
      },
      waitForWidget() {
        if (!checkIsKeratinProduct()) return;

        const checkWidget = () => document.querySelector(WIDGET_SELECTORS);
        if (checkWidget()) {
          this.enable();
          this.setupBuyItNowManagement();
          this.setupDeliveryTextUpdate();
          this.setupPopupContentUpdate();
          return;
        }

        let attempts = 0;
        const maxAttempts = 50;

        const interval = setInterval(() => {
          if (checkWidget() || ++attempts >= maxAttempts) {
            clearInterval(interval);
            this.enable();
            this.setupBuyItNowManagement();
            this.setupDeliveryTextUpdate();
            this.setupPopupContentUpdate();
          }
        }, 200);

        // DOM observer for dynamic loading
        if (MutationObserver) {
          const observer = new MutationObserver(() => {
            if (checkWidget()) {
              observer.disconnect();
              clearInterval(interval);
              this.enable();
              this.setupBuyItNowManagement();
              this.setupDeliveryTextUpdate();
              this.setupPopupContentUpdate();
            }
          });
          observer.observe(document.body, { childList: true, subtree: true });
        }
      },

      setupBuyItNowManagement() {
        if (!checkIsKeratinProduct()) return;

        const manageBuyItNow = () => {
          const buyItNowContainer = document.querySelector(
            "shopify-buy-it-now-button"
          );
          if (!buyItNowContainer) {
            setTimeout(manageBuyItNow, 1000);
            return;
          }

          const updateVisibility = () => {
            // Check if one-time purchase is selected
            const oneTimeChecked = document.querySelector(
              ONE_TIME_PURCHASE_SELECTORS
            );
            const oneTimeActive = document.querySelector(
              ONE_TIME_ACTIVE_SELECTORS
            );
            const isOneTimeSelected = oneTimeChecked || oneTimeActive;

            if (isOneTimeSelected) {
              // Subscription selected - SHOW Buy It Now button
              buyItNowContainer.style.display = "block";
              buyItNowContainer.style.visibility = "visible";
              buyItNowContainer.removeAttribute("hidden");
            } else {
              // One-time selected - HIDE Buy It Now button
              buyItNowContainer.style.display = "none";
              buyItNowContainer.style.visibility = "hidden";
              buyItNowContainer.setAttribute("hidden", "true");
            }
          };

          // Show by default (since subscription is default)
          buyItNowContainer.style.display = "block";
          buyItNowContainer.style.visibility = "visible";
          buyItNowContainer.removeAttribute("hidden");

          // Listen for widget changes
          document.addEventListener("change", (e) => {
            if (
              e.target.matches(
                'input[name*="selling_plan"], input[value*="onetime"], input[value*="subsave"]'
              )
            ) {
              setTimeout(updateVisibility, 50);
            }
          });

          document.addEventListener("click", (e) => {
            if (
              e.target.matches(
                '.rc_widget__option, .rc-option, label[for*="onetime"], label[for*="subsave"], [data-plan-option]'
              )
            ) {
              setTimeout(updateVisibility, 50);
            }
          });

          // Also listen for any input changes in the widget area
          const widgetContainers = document.querySelectorAll(WIDGET_SELECTORS);
          widgetContainers.forEach((container) => {
            container.addEventListener("click", () => {
              setTimeout(updateVisibility, 100);
            });
            container.addEventListener("change", () => {
              setTimeout(updateVisibility, 100);
            });
          });

          setTimeout(updateVisibility, 100);
        };

        setTimeout(manageBuyItNow, 1000);
      },

      setupDeliveryTextUpdate() {
        if (!checkIsKeratinProduct()) return;

        const updateDeliveryText = () => {
          const deliveryLabel = document.querySelector(
            "label[data-plans-label]"
          );
          if (
            deliveryLabel &&
            deliveryLabel.textContent.includes("Delivery every 3 months") &&
            !deliveryLabel.textContent.includes("Cancel anytime")
          ) {
            deliveryLabel.textContent =
              "Delivery every 3 months. Cancel anytime";
          }
          const deliveryOption = document.querySelector(
            'option[data-plan-option*="Delivery every 3 months"]'
          );
          if (
            deliveryOption &&
            deliveryOption.textContent.includes("Delivery every 3 months") &&
            !deliveryOption.textContent.includes("Cancel anytime")
          ) {
            deliveryOption.textContent =
              "Delivery every 3 months. Cancel anytime";
            deliveryOption.setAttribute(
              "data-plan-option",
              "Delivery every 3 months. Cancel anytime"
            );
          }
        };

        updateDeliveryText();
        setTimeout(updateDeliveryText, 500);
        setTimeout(updateDeliveryText, 1500);

        // Monitor for changes in case the widget reloads
        if (MutationObserver) {
          const observer = new MutationObserver(() => {
            setTimeout(updateDeliveryText, 100);
          });

          const subscriptionAreas = document.querySelectorAll(WIDGET_SELECTORS);
          subscriptionAreas.forEach((area) => {
            observer.observe(area, { childList: true, subtree: true });
          });
        }
      },
      setupPopupContentUpdate() {
        if (!checkIsKeratinProduct()) return;

        const manageBuyerConsent = () => {
          const popupContent = document.querySelector("#shopify-buyer-consent");
          if (!popupContent) {
            setTimeout(manageBuyerConsent, 1000);
            return;
          }

          // Update content
          rafUpdate(() => {
            popupContent.innerHTML = `
              <p>When you purchase a <strong>subscription</strong> you'll receive repeat deliveries. These are based on the frequency displayed on the product page. Your payment details will be stored securely and you'll be charged for each of these deliveries.</p>
              <p>If you want to <strong>cancel or change your subscription</strong>, you can do it at any time by contacting our customer support at <a href="mailto:ask@answrbeauty.com" style="color: #007acc; text-decoration: underline;">ask@answrbeauty.com</a></p>
            `;
          });

          const updateVisibility = () => {
            // Check if one-time purchase is selected
            const oneTimeChecked = document.querySelector(
              ONE_TIME_PURCHASE_SELECTORS
            );
            const oneTimeActive = document.querySelector(
              ONE_TIME_ACTIVE_SELECTORS
            );
            const isOneTimeSelected = oneTimeChecked || oneTimeActive;

            if (isOneTimeSelected) {
              // One-time selected - HIDE buyer consent
              popupContent.classList.add("hidden");
              popupContent.setAttribute("aria-hidden", "true");
              popupContent.style.display = "none";
              popupContent.style.visibility = "hidden";
              popupContent.setAttribute("hidden", "true");
            } else {
              // Subscription selected - SHOW buyer consent
              popupContent.classList.remove("hidden");
              popupContent.removeAttribute("aria-hidden");
              popupContent.style.display = "block";
              popupContent.style.visibility = "visible";
              popupContent.removeAttribute("hidden");
            }
          };

          // Show by default (since subscription is default)
          popupContent.classList.remove("hidden");
          popupContent.removeAttribute("aria-hidden");
          popupContent.style.display = "block";
          popupContent.style.visibility = "visible";
          popupContent.removeAttribute("hidden");

          // Listen for widget changes
          document.addEventListener("change", (e) => {
            if (
              e.target.matches(
                'input[name*="selling_plan"], input[value*="onetime"], input[value*="subsave"]'
              )
            ) {
              setTimeout(updateVisibility, 50);
            }
          });

          document.addEventListener("click", (e) => {
            if (
              e.target.matches(
                '.rc_widget__option, .rc-option, label[for*="onetime"], label[for*="subsave"], [data-plan-option]'
              )
            ) {
              setTimeout(updateVisibility, 50);
            }
          });

          // Also listen for any input changes in the widget area
          const widgetContainers = document.querySelectorAll(WIDGET_SELECTORS);
          widgetContainers.forEach((container) => {
            container.addEventListener("click", () => {
              setTimeout(updateVisibility, 100);
            });
            container.addEventListener("change", () => {
              setTimeout(updateVisibility, 100);
            });
          });

          setTimeout(updateVisibility, 100);
        };
        setTimeout(manageBuyerConsent, 1000);
      },
    };
  })();

  /* ═══════════════════════════════════════════════════════════════════
     CORE CART FUNCTIONS (gift detection, adding, orphan removal)
  ═══════════════════════════════════════════════════════════════════ */
  const hasGiftInCart = async (giftVariantId) => {
    try {
      const cart = await cartGET();
      return cart.items.some(
        (item) =>
          parseInt(item.variant_id) === parseInt(giftVariantId) &&
          item.properties &&
          item.properties._gift
      );
    } catch (e) {
      return false;
    }
  };

  const addGiftToCart = async (
    giftVariantId,
    giftTitle,
    productId,
    context = "auto"
  ) => {
    try {
      await cartADD({
        items: [
          {
            id: parseInt(giftVariantId),
            quantity: 1,
            properties: {
              _gift: `subscription (${context})`,
              _gift_title: giftTitle || "Free Gift",
              _gift_for_product: productId,
            },
          },
        ],
      });

      giftMappings.save(giftVariantId, productId, giftTitle);
      return true;
    } catch (e) {
      return false;
    }
  };

  // New function to add items with free products first
  const addToCartWithGiftFirst = async (
    mainItems,
    giftVariantId,
    giftTitle,
    productId
  ) => {
    try {
      // Performance: Console log removed

      // Set flag to prevent fetch wrapper interference
      isCustomCartOperation = true;

      // Check if gift is already in cart
      const hasGift = await hasGiftInCart(giftVariantId);
      // Performance: Console log removed

      // Prepare all items to add
      const itemsToAdd = [];

      // Add gift first if not already in cart
      if (!hasGift && giftVariantId) {
        const giftItem = {
          id: parseInt(giftVariantId),
          quantity: 1,
          properties: {
            _gift: `subscription (gift-first)`,
            _gift_title: giftTitle || "Free Gift",
            _gift_for_product: productId,
          },
        };
        itemsToAdd.push(giftItem);
        // Performance: Console log removed
      }

      // Add main items
      if (mainItems && mainItems.length > 0) {
        itemsToAdd.push(...mainItems);
        // Performance: Console log removed
      }

      // Performance: Console log removed

      // Add all items in one request if we have items to add
      if (itemsToAdd.length > 0) {
        const result = await cartADD({ items: itemsToAdd });
        // Performance: Console log removed

        // Save gift mapping if we added a gift
        if (!hasGift && giftVariantId) {
          giftMappings.save(giftVariantId, productId, giftTitle);
          // Performance: Console log removed
        }
      }

      return true;
    } catch (e) {
      // Performance: Console log removed
      return false;
    } finally {
      // Reset flag
      isCustomCartOperation = false;
    }
  };

  const removeOrphanedGifts = async () => {
    try {
      const cart = await cartGET();
      const subscriptionProducts = new Set();
      const storedMappings = giftMappings.get();
      const updates = {};
      let removeCount = 0;

      // Find subscription products
      cart.items.forEach((item) => {
        if (item.selling_plan_allocation && item.product_id) {
          subscriptionProducts.add(item.product_id);
        }
      });

      // Check each gift item
      cart.items.forEach((item) => {
        if (item.properties && item.properties._gift) {
          let associatedProductId = null;

          // Check product ID in properties first
          if (item.properties._gift_for_product) {
            associatedProductId = parseInt(item.properties._gift_for_product);
          }
          // Check stored mappings
          else if (storedMappings[item.variant_id]) {
            associatedProductId = storedMappings[item.variant_id].productId;
          }
          // Check current page gift
          else {
            const pageGift = getCurrentPageGift();
            if (
              pageGift &&
              parseInt(item.variant_id) === parseInt(pageGift.giftVariantId)
            ) {
              associatedProductId = pageGift.productId;
            }
          }

          // Determine if should remove
          const shouldRemove = associatedProductId
            ? !subscriptionProducts.has(associatedProductId)
            : subscriptionProducts.size === 0;

          if (shouldRemove) {
            updates[item.id] = 0;
            removeCount++;
            if (associatedProductId) {
              giftMappings.remove(item.variant_id);
            }
          }
        }
      });
      if (removeCount > 0) {
        await cartUPDATE(updates);
        reloadSafeguard.incrementAndReload();
      }
    } catch (error) {}
  };

  // Create debounced version for performance
  const debouncedRemoveOrphans = debounce(removeOrphanedGifts, 300);

  /* ═══════════════════════════════════════════════════════════════════
     FETCH WRAPPER (intercepts cart operations for gift management)
  ═══════════════════════════════════════════════════════════════════ */
  const nativeFetch = window.fetch;
  window.fetch = async function (input, init) {
    const url = typeof input === "string" ? input : input.url;

    // Performance optimization: Early exit for non-cart URLs
    if (!url || url.indexOf("/cart") === -1) {
      return nativeFetch.call(this, input, init);
    }

    const isCartAdd = url.endsWith("/cart/add.js");
    const isCartChange =
      url.endsWith("/cart/change.js") ||
      url.endsWith("/cart/update.js") ||
      url.endsWith("/cart/clear.js") ||
      (url.indexOf("/cart") !== -1 && init?.method === "POST");

    const promise = nativeFetch.call(this, input, init);

    if (isCartAdd && !isCustomCartOperation) {
      promise
        .then(async (response) => {
          try {
            const addResult = await response.clone().json();
            const pageGift = getCurrentPageGift();

            if (!pageGift) return;

            const addedItems = Array.isArray(addResult)
              ? addResult
              : addResult.items
              ? addResult.items
              : addResult.id
              ? [addResult]
              : [];

            for (const item of addedItems) {
              if (
                item.selling_plan_allocation &&
                item.product_id === pageGift.productId
              ) {
                if (!(await hasGiftInCart(pageGift.giftVariantId))) {
                  await addGiftToCart(
                    pageGift.giftVariantId,
                    pageGift.giftTitle,
                    pageGift.productId,
                    "fetch-wrap"
                  );
                }
              }
            }
          } catch (e) {}
        })
        .catch(() => {});
    }

    if (isCartChange) {
      promise.finally(() => debouncedRemoveOrphans());
    }
    return promise;
  };

  /* ═══════════════════════════════════════════════════════════════════
     CART CHANGE MONITORING (events, clicks, mutations)
  ═══════════════════════════════════════════════════════════════════ */
  const cartMonitoring = (() => {
    const removeGiftHandler = () => debouncedRemoveOrphans();

    return {
      setup() {
        // Event listeners
        document.addEventListener("cart:updated", removeGiftHandler);
        document.addEventListener("cart:changed", removeGiftHandler);

        // Click monitoring (delegated)
        document.addEventListener("click", (e) => {
          if (e.target.closest(REMOVE_BUTTON_SELECTORS)) {
            removeGiftHandler();
            debouncedRemoveOrphans();
          }
        });

        // Form monitoring
        document.addEventListener("submit", (e) => {
          const form = e.target;
          if (form.action?.indexOf("/cart") !== -1 || form.method === "POST") {
            debouncedRemoveOrphans();
          }
        });

        // Input monitoring
        document.addEventListener("change", (e) => {
          const input = e.target;
          if (
            input.type === "number" &&
            (input.name.indexOf("quantity") !== -1 ||
              input.name.indexOf("updates") !== -1)
          ) {
            debouncedRemoveOrphans();
          }
        });

        // jQuery AJAX monitoring
        if (window.$?.ajaxComplete) {
          $(document).ajaxComplete((event, xhr, settings) => {
            if (settings.url?.indexOf("/cart") !== -1) {
              removeGiftHandler();
            }
          });
        }

        // Storage monitoring
        window.addEventListener(
          "storage",
          (e) => {
            if (e.key?.indexOf("cart") !== -1) {
              removeGiftHandler();
            }
          },
          { passive: true }
        );

        // DOM mutation monitoring
        if (MutationObserver) {
          const observer = new MutationObserver((mutations) => {
            if (
              mutations.some(
                (m) =>
                  m.target.classList?.contains("cart") ||
                  m.target.classList?.contains("cart-drawer") ||
                  m.target.id === "cart" ||
                  m.target.id === "CartDrawer"
              )
            ) {
              removeGiftHandler();
            }
          });

          document.querySelectorAll(CART_ELEMENT_SELECTORS).forEach((el) => {
            observer.observe(el, { childList: true, subtree: true });
          });
        }
      },
    };
  })();

  /* ═══════════════════════════════════════════════════════════════════
     CART AUDIT & SELF-HEALING (checks and fixes cart state)
  ═══════════════════════════════════════════════════════════════════ */
  const auditCart = async () => {
    try {
      const cart = await cartGET();
      const pageGift = getCurrentPageGift();
      const subscriptionProducts = new Set();

      cart.items.forEach((item) => {
        if (item.selling_plan_allocation && item.product_id) {
          subscriptionProducts.add(item.product_id);
        }
      });

      let needsReload = false;

      // Add missing gift if current page has gift mapping
      if (pageGift && subscriptionProducts.has(pageGift.productId)) {
        const hasGift = await hasGiftInCart(pageGift.giftVariantId);
        if (!hasGift) {
          await addGiftToCart(
            pageGift.giftVariantId,
            pageGift.giftTitle,
            pageGift.productId,
            "audit"
          );
          needsReload = true;
        }
      }

      // Remove orphaned gifts
      await removeOrphanedGifts();
      if (needsReload) {
        reloadSafeguard.incrementAndReload();
      }
    } catch (error) {}
  };

  /* ═══════════════════════════════════════════════════════════════════
     PERIODIC MONITORING (fallback cart change detection)
  ═══════════════════════════════════════════════════════════════════ */
  const periodicMonitoring = (() => {
    let lastCartItemCount = null;
    let lastCartItems = null;
    let cartCheckInterval = null;
    let lastInteraction = Date.now();
    let isActive = true;
    let currentInterval = 2000;

    // Track user activity
    const updateActivity = () => {
      lastInteraction = Date.now();
      if (!isActive) {
        isActive = true;
        // Restart with active interval
        if (cartCheckInterval) {
          clearInterval(cartCheckInterval);
          cartCheckInterval = setInterval(checkCartPeriodically, 2000);
          currentInterval = 2000;
        }
      }
    };

    // Debounced mousemove handler
    let mouseMoveTimer;
    const debouncedMouseMove = () => {
      clearTimeout(mouseMoveTimer);
      mouseMoveTimer = setTimeout(updateActivity, 1000);
    };

    // Add activity listeners
    document.addEventListener("click", updateActivity);
    document.addEventListener("input", updateActivity);
    document.addEventListener("mousemove", debouncedMouseMove, {
      passive: true,
    });
    document.addEventListener("keypress", updateActivity);
    document.addEventListener("scroll", updateActivity, { passive: true });

    // Add touch listeners for mobile activity detection
    document.addEventListener("touchstart", updateActivity, { passive: true });
    document.addEventListener("touchmove", debouncedMouseMove, {
      passive: true,
    });

    const checkCartPeriodically = async () => {
      try {
        // Check if user is idle (no interaction for 30 seconds)
        const now = Date.now();
        if (now - lastInteraction > 30000 && isActive) {
          isActive = false;
          // Switch to idle interval (10 seconds)
          clearInterval(cartCheckInterval);
          cartCheckInterval = setInterval(checkCartPeriodically, 10000);
          currentInterval = 10000;
        }

        const cart = await cartGET();
        const currentCount = cart.items.length;
        const currentItemsSignature = cart.items
          .map((item) => `${item.variant_id}:${item.quantity}`)
          .join(",");

        if (
          lastCartItemCount !== null &&
          (lastCartItemCount !== currentCount ||
            lastCartItems !== currentItemsSignature)
        ) {
          debouncedRemoveOrphans();
        }

        lastCartItemCount = currentCount;
        lastCartItems = currentItemsSignature;
      } catch (e) {}
    };

    return {
      start() {
        if (!cartCheckInterval) {
          cartCheckInterval = setInterval(
            checkCartPeriodically,
            currentInterval
          );
        }
      },

      stop() {
        if (cartCheckInterval) {
          clearInterval(cartCheckInterval);
          cartCheckInterval = null;
        }
      },
    };
  })();

  /* ═══════════════════════════════════════════════════════════════════
     GIFT DISPLAY ENHANCEMENT (visual gift presentation)
  ═══════════════════════════════════════════════════════════════════ */
  const giftDisplay = (() => {
    const createGiftDisplay = () => {
      if (!window.giftDisplayData) return "";

      const data = window.giftDisplayData;
      const formatPrice = (price) => {
        const formatted = (price / 100).toFixed(2);
        return `${data.currency === "GBP" ? "£" : data.currency}${formatted}`;
      };

      return `
        <div class="gift-offer-display">
          <div class="gift-products-container">
            <div class="gift-product-item">
              <img src="${data.mainProduct.image}" alt="${
        data.mainProduct.title
      }" class="gift-product-image">
              <div class="gift-product-title">${data.mainProduct.title}</div>
              <div class="gift-product-price">${formatPrice(
                data.mainProduct.price
              )}</div>
            </div>
            
            <div class="gift-plus-sign">+</div>
            
            <div class="gift-product-item">
              <img src="${data.giftProduct.image}" alt="${
        data.giftProduct.title
      }" class="gift-product-image">
              <div class="gift-product-title">Free ${
                data.giftProduct.title
              }</div>
              <div class="gift-product-price_free">
                <div class="gift-crossed-price">${formatPrice(
                  data.giftProduct.comparePrice
                )}</div>
                <div class="gift-free-tag">FREE!</div>
              </div>
            </div>
          </div>
          
          <div class="gift-offer-text">
            <div class="gift-offer-title">Limited Time Offer!</div>
            <div class="gift-offer-description">
              Subscribe to the ${data.mainProduct.title} today and receive a 
              <span class="gift-free-highlight">FREE</span> 
              ${data.giftProduct.title} worth ${formatPrice(
        data.giftProduct.comparePrice
      )}!
            </div>
          </div>
        </div>
      `;
    };

    const enhanceWidgetElements = (container) => {
      const subscriptionLabels = container.querySelectorAll(
        "label[data-label-subsave]"
      );

      subscriptionLabels.forEach((label) => {
        const discountElement = label.querySelector(".rc-option__discount");
        const priceElement = label.querySelector(".rc-option__price");

        if (
          discountElement &&
          priceElement &&
          !label.querySelector(".rc-option__discount__and__price")
        ) {
          const wrapper = document.createElement("div");
          wrapper.className = "rc-option__discount__and__price";

          if (window.giftDisplayData && !discountElement.textContent.trim()) {
            const totalCompare = window.giftDisplayData.totalComparePrice;
            const currency =
              window.giftDisplayData.currency === "GBP"
                ? "£"
                : window.giftDisplayData.currency;
            discountElement.textContent = `${currency}${(
              totalCompare / 100
            ).toFixed(2)}`;
          }

          const discountClone = discountElement.cloneNode(true);
          const priceClone = priceElement.cloneNode(true);

          wrapper.appendChild(discountClone);
          wrapper.appendChild(priceClone);

          const parent = discountElement.parentNode;
          parent.insertBefore(wrapper, discountElement);
          parent.removeChild(discountElement);
          parent.removeChild(priceElement);
        }
      });
    };

    const enhanceSubscriptionWidget = () => {
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === 1) {
              enhanceWidgetElements(node);
            }
          });
        });
      });

      enhanceWidgetElements(document);
      observer.observe(document.body, { childList: true, subtree: true });
    };

    const insertGiftDisplay = () => {
      const target = document.querySelector(".rc_widget__option__selector");

      if (target && !document.querySelector(".gift-offer-display")) {
        target.insertAdjacentHTML("afterend", createGiftDisplay());
        return true;
      }
      return false;
    };

    return {
      initialize() {
        if (!window.giftDisplayData) return;

        const tryInsert = (attempt = 1) => {
          const success = insertGiftDisplay();
          if (success) {
            enhanceSubscriptionWidget();
          } else if (attempt < 5) {
            setTimeout(() => tryInsert(attempt + 1), 500);
          }
        };

        tryInsert();
      },
    };
  })();

  /* ═══════════════════════════════════════════════════════════════════
     INITIALIZATION & SETUP
  ═══════════════════════════════════════════════════════════════════ */
  const initialize = () => {
    // Set up cart monitoring
    cartMonitoring.setup();

    // Start periodic monitoring if page is visible
    if (!document.hidden) {
      periodicMonitoring.start();
    }

    // Handle visibility changes
    document.addEventListener("visibilitychange", () => {
      if (document.hidden) {
        periodicMonitoring.stop();
      } else {
        periodicMonitoring.start();
      }
    });

    // Handle page cache restoration
    window.addEventListener(
      "pageshow",
      (event) => {
        if (event.persisted) {
          auditCart();
        }
      },
      { passive: true }
    );

    // Initialize button management for keratin product
    if (checkIsKeratinProduct()) {
      buttonManager.disable();
      buttonManager.waitForWidget();
    }

    // Initialize gift display enhancement
    giftDisplay.initialize();

    // Run initial audit
    auditCart();
  };

  // Initialize when ready
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initialize);
  } else {
    initialize();
  }

  // Also run audit when page is fully loaded
  if (document.readyState !== "complete") {
    document.addEventListener("readystatechange", () => {
      if (document.readyState === "complete") {
        auditCart();
        // Try gift display again after full page load
        setTimeout(() => giftDisplay.initialize(), 1000);
      }
    });
  }

  // Additional gift display initialization after window load
  window.addEventListener(
    "load",
    () => {
      setTimeout(() => giftDisplay.initialize(), 1000);
    },
    { passive: true }
  );
})();
